import { urlsWrapper } from 'config';
import { createSelector } from 'reselect';
import { combineReducers } from 'redux';
import { isEqual } from 'es-toolkit/compat';
import { getItemById, serialize } from 'utils';
import { getWorkTypesSelector } from 'modules/workTypes';
import { getFieldsSelector } from 'modules/fields';
import { getCropsSelector } from 'modules/crops';
import { getFilterSeasonSelector } from 'modules/globalFilters';
import { getSowing } from 'utils/getSowingCropId';
import getFieldAreaInfo from 'utils/getFieldAreaInfo';
import { workTypeValues } from 'utils/workTypes';
import {
  endOfLocalDay,
  formatLocalDateTimeWithOffset,
  isValidDate,
  startOfLocalDay,
} from 'utils/nativeDate';
import {
  saveData,
  createCommonListConstants,
  createCommonListReducer,
  createCommonItemConstants,
  createCommonItemReducer,
} from './utils';

export const FORM_NAME = 'ControlWorksForm';

const actions = {
  CONTROL_WORK_RECORDS: 'CONTROL_WORK_RECORDS',
  CONTROL_WORK_RECORDS_SEARCH_TEXT: 'CONTROL_WORK_RECORDS_SEARCH_TEXT',
  TOGGLE_FILTERS: 'TOGGLE_FILTERS',
  CLEAR_FILTERS: 'CLEAR_FILTERS',
  SET_FILTER: 'SET_FILTER',
};

const constantsControlWorkrecords = createCommonListConstants(
  actions.CONTROL_WORK_RECORDS
);

const contstantsControlWorkrecordsSearchText = createCommonItemConstants(
  actions.CONTROL_WORK_RECORDS_SEARCH_TEXT
);

export const getInitialFilters = () => ({
  crop: null,
  workType: null,
  workStatus: null,
  hasMachinery: false,
});

export const getInitialState = () => ({
  isOpenFilters: false,
  filters: getInitialFilters(),
});

export const commonReducer = (state = getInitialState(), action) => {
  switch (action.type) {
    case actions.TOGGLE_FILTERS:
      return {
        ...state,
        isOpenFilters:
          typeof action.payload === 'boolean'
            ? action.payload
            : !state.isOpenFilters,
      };
    case actions.CLEAR_FILTERS:
      return {
        ...state,
        filters: getInitialFilters(),
      };
    case actions.SET_FILTER:
      return {
        ...state,
        filters: {
          ...state.filters,
          ...action.payload,
        },
      };
    default:
      return state;
  }
};

export default combineReducers({
  workrecords: createCommonListReducer(constantsControlWorkrecords),
  searchText: createCommonItemReducer(contstantsControlWorkrecordsSearchText),
  common: commonReducer,
});

export function getControlWorkRecords(params) {
  return saveData(
    `${urlsWrapper('workrecords')}?${serialize(params)}`,
    constantsControlWorkrecords.SET_LIST,
    constantsControlWorkrecords.SET_IS_PENDING
  );
}

export const updateControlWorkRecord =
  ({ date, divisionIds }) =>
  (dispatch) => {
    dispatch(
      getControlWorkRecords({
        start_dt: formatLocalDateTimeWithOffset(startOfLocalDay(date)),
        end_dt: formatLocalDateTimeWithOffset(endOfLocalDay(date)),
        embed: 'machinery',
        ...(divisionIds?.length && { division_ids: divisionIds.join(',') }),
        only_assigned: true,
      })
    );
  };

export const getControlWorkRecordsSelector = createSelector(
  [
    (state) => state.controlWorkrecords.workrecords.list,
    (state) => state.controlWorkrecords.workrecords.isPending,
  ],
  (list, isPending) => ({
    list,
    isPending,
  })
);

export const setSearchText = (payload) => ({
  type: contstantsControlWorkrecordsSearchText.SET_ITEM,
  payload,
});

export const getSearchTextSelector = (state) =>
  state.controlWorkrecords.searchText.item;

export const getControlWorksFormSelector = createSelector(
  [(state) => state.form?.[FORM_NAME]?.values],
  (value) => value
);

export const getControlWorksFormDateSelector = createSelector(
  [getControlWorksFormSelector],
  (formValues) => formValues?.date
);

export const getControlWorksFormSeasonSelector = createSelector(
  [getControlWorksFormDateSelector],
  (formValues) => {
    const date = formValues?.date;
    return isValidDate(date)
      ? startOfLocalDay(date).getFullYear()
      : new Date().getFullYear();
  }
);

export const setToggleFilters = (payload) => ({
  type: actions.TOGGLE_FILTERS,
  payload,
});

export const setClearFilters = (payload) => ({
  type: actions.CLEAR_FILTERS,
  payload,
});

export const setFilter = (payload) => ({
  type: actions.SET_FILTER,
  payload,
});

export const getIsOpenFiltersSelector = (state) =>
  state.controlWorkrecords.common.isOpenFilters;

export const getFiltersSelector = (state) =>
  state.controlWorkrecords.common.filters;

const getDescription = (field, crops, season) => {
  if (!field) return '';
  const sowing = getSowing(field, season);
  const crop = getItemById(crops, sowing?.crop_id);
  const cropInfo = crop == null ? 'Культура не указана' : crop.name;
  return [`${cropInfo}${getFieldAreaInfo(field, true, season)}`];
};

export const getControlWorksSelector = createSelector(
  [
    getControlWorkRecordsSelector,
    getWorkTypesSelector,
    getFieldsSelector,
    getCropsSelector,
    getFilterSeasonSelector,
  ],
  (
    { list: works },
    { list: workTypes },
    { list: fields },
    { list: crops },
    season
  ) => {
    const preparedWorks = works.map((w) => {
      const field = fields.find(({ id }) => id === w.field_id);
      const sowing = getSowing(field, season);
      const currentSowing = getSowing(field, field?.season);
      return {
        ...w,
        workType: getItemById(workTypes, w.work_type_id),
        field: fields.find(({ id }) => id === w.field_id),
        description: getDescription(field, crops, season),
        sowing,
        crop: getItemById(crops, sowing?.crop_id),
        currentSowing,
        currentCrop: getItemById(crops, currentSowing?.crop_id),
      };
    });
    return preparedWorks.filter(
      ({ workType }) => workType?.value !== workTypeValues.WORK_TYPE_COATING
    );
  }
);

export const getFilteredControlWorksPanelSelector = createSelector(
  [getControlWorksSelector, getFiltersSelector],
  (works, filters) => {
    const { crop, workType } = filters;
    if (!crop && !workType) return works;

    return works.filter((item) => {
      let cropMatch = true;
      let workTypeMatch = true;

      if (crop) {
        cropMatch = item?.currentCrop?.id === crop;
      }
      if (workType) {
        workTypeMatch = item?.workType?.work_type_group_value === workType;
      }

      return cropMatch && workTypeMatch;
    });
  }
);

export const getFilteredControlWorksSelector = createSelector(
  [getFilteredControlWorksPanelSelector, getFiltersSelector],
  (works, filters) => {
    const { workStatus, hasMachinery } = filters;
    if (!workStatus && !hasMachinery) return works;
    return works.filter((item) => {
      let workStatusMatch = true;
      let hasMachineryMatch = true;
      if (workStatus) {
        workStatusMatch = item?.work_status === workStatus;
      }
      if (hasMachinery) {
        hasMachineryMatch = !!item?.machinery?.length;
      }
      return workStatusMatch && hasMachineryMatch;
    });
  }
);

export const getIsChangedFiltersSelector = createSelector(
  [getFiltersSelector],
  (filters) => {
    return !isEqual(filters, getInitialFilters());
  }
);

export const getIsPendingControlWorksSelector = createSelector(
  [
    getControlWorkRecordsSelector,
    getWorkTypesSelector,
    getFieldsSelector,
    getCropsSelector,
  ],
  (works, workTypes, fields, crops) => {
    return (
      works.isPending ||
      workTypes.isPending ||
      fields.isPending ||
      crops.isPending
    );
  }
);
